/**
 * Contractor Slide-over Alpine.js Component
 *
 * Manages the contractor detail slide-over panel with tabs:
 * Overview, Jobs, Account. Supports view mode (existing contractor)
 * and create mode (new contractor).
 *
 * Follows the Landlord/Tenancy slideover pattern with embedded
 * account tab via accountTabMixin.
 */

import consumer from "../channels/consumer"
import { notesMixin, destroyNote } from './notes_mixin'
import { accountTabMixin } from './account_tab_mixin'
import { legacyImportMixin } from './legacy_import_mixin'
import { transactionSearchMixin } from './transaction_search_mixin'

export function contractorSlideover() {
  const searchMixin = transactionSearchMixin('contractor')
  const _performTransactionSearch = searchMixin.performSearch

  return {
    ...notesMixin,
    ...accountTabMixin('contractor'),
    ...legacyImportMixin('contractor'),
    ...searchMixin,

    // Override performSearch to be tab-aware (invoices vs transactions)
    async performSearch() {
      if (this.activeTab === 'account' && this.acctSubTab === 'invoices') {
        this._performInvoiceSearch()
      } else {
        return _performTransactionSearch.call(this)
      }
    },

    // Client-side invoice search
    _performInvoiceSearch() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) {
        this.searchMatches = []
        this.searchMatchIndex = -1
        this.searchTotalMatches = 0
        this.highlightedTxnId = null
        return
      }

      const matches = this.invoices.filter(inv => {
        const desc = (inv.description || '').toLowerCase()
        const property = (inv.property_name || '').toLowerCase()
        const reference = `#c${inv.id}`
        const amount = this.formatPence(inv.amount_pence).toLowerCase()
        const status = (inv.status_label || '').toLowerCase()
        const date = this.formatDate(inv.date).toLowerCase()
        return desc.includes(query) || property.includes(query) || reference.includes(query) ||
          amount.includes(query) || status.includes(query) || date.includes(query)
      })

      this.searchMatches = matches.map(m => ({ id: `inv-${m.id}` }))
      this.searchTotalMatches = matches.length

      if (matches.length > 0) {
        this.searchMatchIndex = 0
        this.navigateToMatch(0)
      } else {
        this.searchMatchIndex = -1
        this.highlightedTxnId = null
      }
    },

    // State
    open: false,
    loading: false,
    saving: false,
    error: null,
    contractor: null,
    contractorId: null,
    subscription: null,
    zIndex: 100010,

    // Mode: 'view' or 'create'
    mode: 'view',

    // Tab state
    activeTab: 'overview',
    acctSubTab: 'transactions', // 'transactions' or 'invoices'

    // Accordion state (for create form and overview sections)
    openSections: {},

    // Notes
    noteText: '',
    noteSaving: false,
    noteError: null,

    // Edit modal state
    editModalOpen: false,
    editSaving: false,
    editError: null,
    editPasswordRequired: false,
    editForm: {},

    // Create form data
    form: {
      name: '',
      company_name: '',
      email: '',
      phone: '',
      gas_safe_number: '',
      niceic_number: '',
      registration_number: '',
      address_line_1: '',
      address_line_2: '',
      town: '',
      county: '',
      postcode: '',
      bank_account_name: '',
      bank_sort_code: '',
      bank_account_number: '',
      commission_type: 'none',
      default_commission_rate: '',
      commission_fixed_pence: '',
      notes: '',
      work_types: []
    },

    // Documents
    pendingFiles: [],

    // Work type options (populated from server)
    workTypeOptions: {},

    // Documents state
    documentsForm: {
      dragging: false,
      documentsToDelete: [],
      saving: false
    },

    // AI panel state
    aiPanelOpen: false,

    // Initialize component
    init() {
      const self = this

      // Subscribe to ActionCable for real-time updates
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            if (message.type === 'contractor' && self.contractor && message.id === self.contractor.id) {
              self.handleContractorUpdate(message)
            }
          }
        }
      )

      // Track AI panel state
      window.addEventListener('ai-panel-toggled', (e) => {
        this.aiPanelOpen = e.detail.open
      })

      // Watch for open state changes
      this.$watch('open', (value) => {
        if (value && this.contractorId && !this.contractor && this.mode === 'view') {
          this.fetchContractor()
        }
      })

      // Lazy-load account data when Account tab is first selected
      this.$watch('activeTab', (tab) => {
        if (tab === 'account' && !this.acctDataLoaded && this.contractorId) {
          this.fetchAccountData(true)
        }
      })

      // Initialise account tab event listeners
      this.initAccountTab()
    },

    // Open slide-over for a specific contractor (view mode)
    openForContractor(id, tab) {
      const store = Alpine.store?.('slideovers')

      // If already open for this contractor, bring to front
      if (store?.isOpen?.('contractor', id)) {
        store.bringToFront('contractor', id)
        if (tab) this.activeTab = tab
        return
      }

      // Get next z-index from store
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.contractorId = id
      this.contractor = null
      this.error = null
      this.mode = 'view'
      this.activeTab = tab || 'overview'
      this.acctSubTab = 'transactions'
      this.openSections = {}
      this.resetEditModal()
      this.resetAccountState()
      this.clearSearch()
      this.open = true

      // Register this slideover
      if (store?.register) {
        store.register('contractor', id, this)
      }

      this.fetchContractor()

      // Explicitly fetch account data if opening on account tab
      if (this.activeTab === 'account') {
        this.fetchAccountData(true)
      }
    },

    // Open slide-over for creating a new contractor
    openNew() {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.contractorId = null
      this.contractor = null
      this.error = null
      this.mode = 'create'
      this.activeTab = 'overview'
      this.resetForm()
      this.pendingFiles = []
      this.openSections = { basic: true, workTypes: true }
      this.open = true
    },

    // Close slide-over
    close() {
      const store = Alpine.store?.('slideovers')
      if (store?.unregister && this.contractorId) {
        store.unregister('contractor', this.contractorId)
      }

      this.openSections = {}
      this.resetEditModal()
      this.resetAccountState()
      this.clearSearch()
      this.pendingFiles = []
      this.documentsForm = { dragging: false, documentsToDelete: [], saving: false }

      this.$nextTick(() => {
        this.open = false
      })

      window.dispatchEvent(new CustomEvent('account-slideover-closed'))
    },

    // Reset edit modal
    resetEditModal() {
      this.editModalOpen = false
      this.editSaving = false
      this.editError = null
      this.editForm = {}
    },

    // Reset form to defaults (create mode)
    resetForm() {
      this.form = {
        name: '',
        company_name: '',
        email: '',
        phone: '',
        gas_safe_number: '',
        niceic_number: '',
        registration_number: '',
        address_line_1: '',
        address_line_2: '',
        town: '',
        county: '',
        postcode: '',
        bank_account_name: '',
        bank_sort_code: '',
        bank_account_number: '',
        commission_type: 'none',
        default_commission_rate: '',
        commission_fixed_pence: '',
        notes: '',
        work_types: []
      }
    },

    // Fetch contractor data from server
    async fetchContractor(silent = false) {
      if (!this.contractorId) return

      if (!silent) {
        this.loading = true
      }
      this.error = null

      try {
        const response = await fetch(`/contractors/${this.contractorId}.json`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        this.contractor = await response.json()
      } catch (error) {
        console.error('Failed to fetch contractor:', error)
        this.error = 'Failed to load contractor details. Please try again.'
      } finally {
        this.loading = false
      }
    },

    // Handle real-time updates from ActionCable
    handleContractorUpdate(message) {
      if (!this.contractor || message.id !== this.contractor.id) return

      switch (message.action) {
        case 'update':
          Object.assign(this.contractor, message.data)
          break
        case 'destroy':
          this.close()
          break
      }
    },

    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
    },

    // ========== Edit Modal ==========

    openEditModal() {
      if (!this.contractor) return
      this.editForm = {
        name: this.contractor.name || '',
        company_name: this.contractor.company_name || '',
        email: this.contractor.email || '',
        phone: this.contractor.phone || '',
        gas_safe_number: this.contractor.gas_safe_number || '',
        niceic_number: this.contractor.niceic_number || '',
        registration_number: this.contractor.registration_number || '',
        address_line_1: this.contractor.address_line_1 || '',
        address_line_2: this.contractor.address_line_2 || '',
        town: this.contractor.town || '',
        county: this.contractor.county || '',
        postcode: this.contractor.postcode || '',
        bank_account_name: this.contractor.bank_account_name || '',
        bank_sort_code: this.contractor.bank_sort_code || '',
        bank_account_number: this.contractor.bank_account_number || '',
        commission_type: this.contractor.commission_type || 'none',
        default_commission_rate: this.contractor.default_commission_rate || '',
        commission_fixed_pence: this.contractor.commission_fixed_pence || '',
        notes: this.contractor.notes || '',
        work_types: [...(this.contractor.work_types || [])],
        current_password: ''
      }
      // Initialise pounds from pence for the input
      const pence = parseInt(this.contractor.commission_fixed_pence)
      this.editFixedAmountPounds = (!isNaN(pence) && pence > 0) ? (pence / 100).toFixed(2) : ''

      this.editError = null
      this.editPasswordRequired = false
      this.editModalOpen = true
    },

    async saveEdit() {
      // Convert pounds back to pence before saving
      if (this.editForm.commission_type === 'fixed') {
        const pounds = parseFloat(this.editFixedAmountPounds)
        this.editForm.commission_fixed_pence = isNaN(pounds) ? '' : Math.round(pounds * 100)
      }
      this.editSaving = true
      this.editError = null

      try {
        const { current_password, ...contractorFields } = this.editForm
        const response = await fetch(`/contractors/${this.contractorId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ current_password, contractor: contractorFields })
        })

        if (!response.ok) {
          const errorData = await response.json()
          if (errorData.password_required) {
            this.editPasswordRequired = true
          }
          throw new Error(errorData.error || errorData.errors?.join(', ') || 'Failed to save')
        }

        // Refresh contractor data
        await this.fetchContractor(true)

        this.editForm.current_password = ''
        this.editPasswordRequired = false
        this.editModalOpen = false

        // Notify list to update
        window.dispatchEvent(new CustomEvent('contractor-saved', {
          detail: { id: this.contractorId, data: this.contractor, isNew: false }
        }))
      } catch (error) {
        console.error('Failed to save contractor:', error)
        this.editError = error.message || 'Failed to save. Please try again.'
      } finally {
        this.editSaving = false
      }
    },

    // Toggle edit form work type
    toggleEditWorkType(type) {
      const index = this.editForm.work_types.indexOf(type)
      if (index === -1) {
        this.editForm.work_types.push(type)
      } else {
        this.editForm.work_types.splice(index, 1)
      }
    },

    isEditWorkTypeSelected(type) {
      return this.editForm.work_types?.includes(type)
    },

    // ========== Create Mode ==========

    async save() {
      this.saving = true
      this.error = null

      // Convert pounds back to pence before saving
      if (this.form.commission_type === 'fixed') {
        const pounds = parseFloat(this.createFixedAmountPounds)
        this.form.commission_fixed_pence = isNaN(pounds) ? '' : Math.round(pounds * 100)
      }

      try {
        const url = this.contractorId
          ? `/contractors/${this.contractorId}`
          : '/contractors'
        const method = this.contractorId ? 'PATCH' : 'POST'

        let response

        // Use FormData if there are pending files
        if (this.pendingFiles.length > 0) {
          const formData = new FormData()

          Object.keys(this.form).forEach(key => {
            if (key === 'work_types') {
              this.form.work_types.forEach(wt => {
                formData.append('contractor[work_types][]', wt)
              })
              if (this.form.work_types.length === 0) {
                formData.append('contractor[work_types][]', '')
              }
            } else {
              formData.append(`contractor[${key}]`, this.form[key] || '')
            }
          })

          this.pendingFiles.forEach(file => {
            formData.append('contractor[documents][]', file)
          })

          response = await fetch(url, {
            method: method,
            headers: {
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: formData
          })
        } else {
          response = await fetch(url, {
            method: method,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: JSON.stringify({ contractor: this.form })
          })
        }

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.join(', ') || 'Failed to save')
        }

        const contractor = await response.json()

        window.dispatchEvent(new CustomEvent('contractor-saved', {
          detail: { id: contractor.id, data: contractor, isNew: !this.contractorId }
        }))

        this.close()
      } catch (error) {
        console.error('Failed to save contractor:', error)
        this.error = error.message || 'Failed to save. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // Delete contractor
    async deleteContractor() {
      if (!this.contractorId) return
      if (!confirm('Are you sure you want to delete this contractor? This cannot be undone.')) return

      this.saving = true
      this.error = null

      try {
        const response = await fetch(`/contractors/${this.contractorId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          // A refused delete (the contractor has jobs or charges on record)
          // comes back as 422 with the reason in words.
          const data = await response.json().catch(() => ({}))
          throw new Error(data.errors?.join(', ') || 'Failed to delete contractor')
        }

        window.dispatchEvent(new CustomEvent('contractor-deleted', {
          detail: { id: this.contractorId }
        }))

        this.close()
      } catch (error) {
        console.error('Failed to delete contractor:', error)
        this.error = error.message || 'Failed to delete. Please try again.'
      } finally {
        this.saving = false
      }
    },

    // ========== Documents (view mode — grid with upload/delete) ==========

    addDocuments(files) {
      const fileArray = Array.from(files).filter(f =>
        f.type === 'application/pdf' || f.type.startsWith('image/')
      )
      if (fileArray.length === 0) return
      this.uploadDocuments(fileArray)
    },

    async uploadDocuments(files) {
      this.documentsForm.saving = true
      try {
        const formData = new FormData()
        for (const file of files) {
          formData.append('documents[]', file)
        }

        const response = await fetch(`/contractors/${this.contractorId}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchContractor(true)
          this.pollForDocumentThumbnails()
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to upload documents')
        }
      } catch (error) {
        console.error('Failed to upload documents:', error)
        alert('Failed to upload documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    toggleDocumentDeletion(docId) {
      const index = this.documentsForm.documentsToDelete.indexOf(docId)
      if (index > -1) {
        this.documentsForm.documentsToDelete.splice(index, 1)
      } else {
        this.documentsForm.documentsToDelete.push(docId)
      }
    },

    async saveDocuments() {
      if (this.documentsForm.documentsToDelete.length === 0) return

      this.documentsForm.saving = true
      try {
        const formData = new FormData()
        for (const id of this.documentsForm.documentsToDelete) {
          formData.append('documents_to_delete[]', id)
        }

        const response = await fetch(`/contractors/${this.contractorId}/update_documents`, {
          method: 'PATCH',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          await this.fetchContractor(true)
          this.documentsForm.documentsToDelete = []
        } else {
          const data = await response.json().catch(() => ({}))
          alert(data.error || 'Failed to delete documents')
        }
      } catch (error) {
        console.error('Failed to delete documents:', error)
        alert('Failed to delete documents. Please try again.')
      } finally {
        this.documentsForm.saving = false
      }
    },

    // ==========================================
    // Notes (History tab)
    // ==========================================

    async addNote() {
      if (!this.noteText.trim() || this.noteSaving) return

      this.noteSaving = true
      this.noteError = null

      try {
        const newNote = await this.saveNote({ type: 'Contractor', id: this.contractorId }, this.noteText)

        if (this.contractor) {
          if (!this.contractor.activities) {
            this.contractor.activities = []
          }
          this.contractor.activities.unshift(newNote)
        }

        this.noteText = ''
      } catch (error) {
        console.error('Failed to add note:', error)
        this.noteError = error.message || 'Failed to add note. Please try again.'
      } finally {
        this.noteSaving = false
      }
    },
    removeTimelineEntry(entryId) {
      if (this.contractor?.activities) {
        this.contractor.activities = this.contractor.activities.filter(a => a.id !== entryId)
      }
    },

    async deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      try {
        await destroyNote(noteId)
        this.removeTimelineEntry(noteId)
      } catch (error) {
        console.error('Failed to delete note:', error)
        alert('Failed to delete note. Please try again.')
      }
    },

    pollForDocumentThumbnails(attempts = 0) {
      const maxAttempts = 10
      const pollInterval = 3000

      const hasProcessingDocs = this.contractor?.documents?.some(doc => !doc.thumbnail_url && doc.content_type === 'application/pdf')

      if (!hasProcessingDocs || attempts >= maxAttempts || !this.open) {
        return
      }

      setTimeout(async () => {
        if (!this.open) return

        await this.fetchContractor(true)

        const stillProcessing = this.contractor?.documents?.some(doc => !doc.thumbnail_url && doc.content_type === 'application/pdf')
        if (stillProcessing && attempts < maxAttempts - 1) {
          this.pollForDocumentThumbnails(attempts + 1)
        }
      }, pollInterval)
    },

    // ========== File Handling (create mode) ==========

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
      this.pendingFiles = [...this.pendingFiles, ...pdfFiles]
      event.target.value = ''
    },

    removePendingFile(index) {
      this.pendingFiles.splice(index, 1)
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    // ========== Accordion ==========

    toggleSection(section) {
      this.openSections[section] = !this.openSections[section]
    },

    isSectionOpen(section) {
      return !!this.openSections[section]
    },

    // ========== Work Types (create form) ==========

    toggleWorkType(type) {
      const index = this.form.work_types.indexOf(type)
      if (index === -1) {
        this.form.work_types.push(type)
      } else {
        this.form.work_types.splice(index, 1)
      }
    },

    isWorkTypeSelected(type) {
      return this.form.work_types.includes(type)
    },

    // ========== Account Sub-Tabs ==========

    setAcctSubTab(tab) {
      if (tab === this.acctSubTab) return
      this.clearSearch()
      this.searchOpen = false
      this.acctSubTab = tab
    },

    // ========== Computed Properties ==========

    get isEditing() {
      return this.mode === 'view' && !!this.contractorId
    },

    get displayName() {
      if (this.mode === 'view' && this.contractor) {
        return this.contractor.display_name
      }
      if (this.form.company_name) {
        return this.form.name
          ? `${this.form.company_name} (${this.form.name})`
          : this.form.company_name
      }
      return this.form.name || 'New Contractor'
    },

    // Stat value sizing (3 columns: balance, awaiting, jobs)
    get statValueClass() {
      if (!this.contractor) return 'text-2xl'
      const lengths = [
        this.formatPence(this.contractor.balance_pence).length,
        this.formatPence(this.contractor.awaiting_payment_pence).length
      ]
      const max = Math.max(...lengths)
      if (max > 9) return 'text-base'
      if (max > 7) return 'text-lg'
      return 'text-2xl'
    },

    get balanceSubtitle() {
      if (!this.contractor) return ''
      const status = this.contractor.balance_status
      if (status === 'clear') return 'Clear'
      if (status === 'payment_due') return 'Payment due'
      return 'In credit'
    },

    get awaitingPaymentSubtitle() {
      if (!this.contractor) return ''
      if (this.contractor.awaiting_payment_pence === 0) return 'No pending invoices'
      // Use invoice count from account data if loaded, otherwise generic
      if (this.acctDataLoaded && this.invoices.length > 0) {
        const unpaid = this.invoices.filter(i => i.status !== 'paid').length
        if (unpaid === 0) return 'Awaiting settlement'
        return `${unpaid} ${unpaid === 1 ? 'invoice' : 'invoices'} awaiting funds`
      }
      return 'Awaiting settlement'
    },

    get balanceBorderClass() {
      if (!this.contractor) return 'border-gray-800 dark:border-gray-200'
      if (this.contractor.balance_pence < 0) return 'border-red-500'
      return 'border-gray-800 dark:border-gray-200'
    },

    // Temporary pounds values for fixed commission inputs (avoids mid-edit reformatting)
    editFixedAmountPounds: '',
    createFixedAmountPounds: '',

    // Format date with full month name (for jobs tab)
    formatDateLong(dateStr) {
      if (!dateStr) return '-'
      const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    // Work type badge color class
    workTypeBadgeClass(index) {
      const colors = [
        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
        'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
      ]
      return colors[index % colors.length]
    },

    // Job status badge class
    jobStatusClass(color) {
      const map = {
        gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
        purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      }
      return map[color] || map.gray
    },

    // Active vs completed jobs
    // The Issues tab: the record's issues and renewals as the Maintenance list rows them
    get activeJobs() {
      return (this.contractor?.jobs || []).filter(r => r.status === 'open')
    },

    get completedJobs() {
      return (this.contractor?.jobs || []).filter(r => r.status !== 'open')
    },

    openIssueRow(row) {
      if (row.kind === 'renewal') window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { renewal_id: row.id } }))
      else window.dispatchEvent(new CustomEvent('open-issue-slideover', { detail: { id: row.id } }))
    },

    issueToneClass(tone) {
      return { late: 'text-[#B45309]', done: 'text-[#047857]', off: 'text-[#9CA3AF]' }[tone] || 'text-[#374151]'
    },

    formatSortCode(code) {
      if (!code) return ''
      const digits = code.replace(/-/g, '')
      if (digits.length === 6) {
        return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`
      }
      return code
    },

    formatAccountNumber(number) {
      if (!number) return ''
      if (number.length === 8) {
        return `****${number.slice(-4)}`
      }
      return number
    }
  }
}
